<template>
  <div class="personal-page">
    <el-alert
      v-if="needChange"
      :title="$tt('当前为初始密码，请先修改密码后再使用其它功能')"
      type="warning"
      :closable="false"
      show-icon
      class="pwd-alert"
    />

    <div class="identity">
      <el-upload
        action="#"
        :show-file-list="false"
        :http-request="uploadAvatar"
        accept="image/*"
        class="avatar-upload"
      >
        <el-avatar :size="64" :src="form.image_path">
          <i class="el-icon-user-solid"></i>
        </el-avatar>
      </el-upload>
      <div class="identity-text">
        <div class="identity-name">{{ form.cn_name || form.nick_name || $tt("未命名用户") }}</div>
        <div class="identity-sub">
          <span v-if="form.phone">{{ form.phone }}</span>
          <span v-if="form.random_code">{{ $tt("序列号") }} {{ form.random_code }}</span>
          <span v-if="form.create_time">{{ $tt("注册于") }} {{ formatTime(form.create_time) }}</span>
        </div>
        <div class="identity-tip">{{ $tt("点击头像更换（自动压缩为 200×200）") }}</div>
      </div>
    </div>

    <el-tabs v-model="tab" class="personal-tabs">
      <el-tab-pane :label="$tt('账号资料')" name="profile">
        <el-form :model="form" label-width="88px" class="block-form">
          <el-form-item :label="$tt('名称')">
            <el-input v-model="form.cn_name" maxlength="32" show-word-limit />
          </el-form-item>
          <el-form-item :label="$tt('昵称')">
            <el-input v-model="form.nick_name" disabled />
          </el-form-item>
          <el-form-item :label="$tt('性别')">
            <el-radio-group v-model="form.gender">
              <el-radio :label="0">{{ $tt("男") }}</el-radio>
              <el-radio :label="1">{{ $tt("女") }}</el-radio>
              <el-radio :label="2">{{ $tt("未知") }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="$tt('邮箱')">
            <el-input v-model="form.email" :placeholder="$tt('选填')" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="saveProfile">{{ $tt("保存资料") }}</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane :label="$tt('账号安全')" name="security">
        <div class="secure-card">
          <div class="secure-title">{{ $tt("登录密码") }}</div>
          <div class="secure-desc">{{ $tt("通过当前手机短信验证后设置新密码") }}</div>
          <div class="secure-row">
            <el-input v-model="pwd.sms_code" :placeholder="$tt('短信验证码')">
              <el-button slot="append" @click="sendSms(form.phone, 'pwd')">{{ $tt("发送验证码") }}</el-button>
            </el-input>
            <el-input v-model="pwd.password" show-password :placeholder="$tt('新密码，不少于 6 位')" />
            <el-button type="primary" @click="savePassword">{{ $tt("修改密码") }}</el-button>
          </div>
        </div>
        <div class="secure-card">
          <div class="secure-title">{{ $tt("手机号") }}</div>
          <div class="secure-desc">{{ $tt("当前绑定") }} {{ form.phone || $tt("未绑定") }}。{{ $tt("更换需验证原号码和新号码。") }}</div>
          <div class="secure-row">
            <el-input v-model="phoneForm.sms_code" :placeholder="$tt('原手机验证码')">
              <el-button slot="append" @click="sendSms(form.phone, 'old')">{{ $tt("发送") }}</el-button>
            </el-input>
            <el-input v-model="phoneForm.new_phone" :placeholder="$tt('新手机号')" />
            <el-input v-model="phoneForm.new_sms_code" :placeholder="$tt('新手机验证码')">
              <el-button slot="append" @click="sendSms(phoneForm.new_phone, 'new')">{{ $tt("发送") }}</el-button>
            </el-input>
            <el-button type="primary" @click="savePhone">{{ $tt("更换手机") }}</el-button>
          </div>
        </div>
        <div class="secure-card">
          <div class="secure-title">{{ $tt("微信") }}</div>
          <div class="secure-desc">
            {{ form.open_id ? $tt("已绑定微信，解绑后需重新扫码。") : $tt("未绑定。绑定前请先完成短信验证。") }}
          </div>
          <el-button v-if="!form.open_id" type="primary" plain @click="openBindWechat">{{ $tt("绑定微信") }}</el-button>
          <el-button v-else type="danger" plain @click="openUnbindWechat">{{ $tt("解绑微信") }}</el-button>
        </div>
      </el-tab-pane>

      <el-tab-pane :label="$tt('资源用量')" name="quota">
        <div v-loading="quotaLoading">
          <p class="quota-intro">
            {{ $tt("工程个数和存储按账号计算。每个工程的页数、指令 IP 按创建时选择的形态（签名 / 尊贵 / 旗舰）。按钮不限制，只统计已用个数。") }}
          </p>
          <div class="quota-grid">
            <div class="quota-card" v-for="q in quotaItems" :key="q.key">
              <div class="q-label">{{ $tt(q.label) }}</div>
              <div class="q-value">{{ q.usedText }}</div>
              <el-progress
                v-if="q.percent != null"
                :percentage="q.percent"
                :color="q.percent >= 90 ? '#f56c6c' : '#409eff'"
                :stroke-width="8"
              />
              <div class="q-hint">{{ $tt(q.hint) }}</div>
            </div>
          </div>
          <p v-if="extraCountText" class="quota-extra">{{ extraCountText }}</p>
          <div class="recent-head">
            <span>{{ $tt("工程画布用量") }}</span>
            <el-button type="text" @click="goProjects">{{ $tt("全部工程") }}</el-button>
          </div>
          <el-table
            :data="projectUsages"
            border
            :max-height="listTableMaxHeight"
            :empty-text="$tt('暂无工程')"
            :row-class-name="quotaRowClass"
          >
            <el-table-column prop="project_name" :label="$tt('工程')" min-width="180" show-overflow-tooltip />
            <el-table-column prop="type_name" :label="$tt('形态')" width="100" />
            <el-table-column :label="$tt('页面')" width="120">
              <template slot-scope="scope">
                <span :class="{ 'quota-warn-text': scope.row.page_full }">
                  {{ usagePair(scope.row.used_pages, scope.row.quota_pages) }}
                </span>
                <el-tag v-if="scope.row.page_full" size="mini" type="warning" class="quota-tag">{{ $tt("页已满") }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$tt('指令 IP')" width="130">
              <template slot-scope="scope">
                <span :class="{ 'quota-warn-text': scope.row.ip_full }">
                  {{ usagePair(scope.row.used_ips, scope.row.quota_ips) }}
                </span>
                <el-tag v-if="scope.row.ip_full" size="mini" type="warning" class="quota-tag">{{ $tt("IP 已满") }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$tt('按钮')" width="110">
              <template slot-scope="scope">{{ Number(scope.row.used_buttons || 0) }}{{ $tt("（不限）") }}</template>
            </el-table-column>
            <el-table-column label="" width="88">
              <template slot-scope="scope">
                <el-button type="text" @click="goProject(scope.row)">{{ $tt("打开") }}</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :title="$tt('绑定微信')" :visible.sync="bindVisible" width="420px">
      <p>{{ $tt("请先发送短信验证码，再扫码。若该微信已被其它账号占用，需对方先解绑。") }}</p>
      <el-input v-model="bindSms" :placeholder="$tt('短信验证码')">
        <el-button slot="append" @click="sendSms(form.phone, 'bind')">{{ $tt("发送验证码") }}</el-button>
      </el-input>
      <div class="bind-qr" v-if="bindQr">
        <img :src="bindQr" alt="qr" />
      </div>
      <span slot="footer">
        <el-button @click="bindVisible = false">{{ $tt("取消") }}</el-button>
        <el-button type="primary" @click="confirmBindWechat">{{ $tt("确认绑定") }}</el-button>
      </span>
    </el-dialog>
    <el-dialog :title="$tt('解绑微信')" :visible.sync="unbindVisible" width="360px">
      <el-input v-model="unbindSms" :placeholder="$tt('短信验证码')">
        <el-button slot="append" @click="sendSms(form.phone, 'unbind')">{{ $tt("发送验证码") }}</el-button>
      </el-input>
      <span slot="footer">
        <el-button @click="unbindVisible = false">{{ $tt("取消") }}</el-button>
        <el-button type="primary" @click="confirmUnbind">{{ $tt("确认解绑") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { applyAccountQuotaToLogin, compressAvatar, isNeedChangePassword } from "@/utils/account";
import { setCookie } from "@/utils/utils";
import { mapState } from "vuex";
import moment from "moment";

export default {
  listTableTopOffset: 420,
  data() {
    return {
      tab: "profile",
      form: {},
      dashboard: null,
      quotaLoading: false,
      pwd: { sms_code: "", password: "" },
      phoneForm: { sms_code: "", new_phone: "", new_sms_code: "" },
      bindVisible: false,
      unbindVisible: false,
      bindSms: "",
      unbindSms: "",
      bindQr: "",
      bindEventKey: "",
      bindTimer: null,
      scanned: false,
    };
  },
  computed: {
    ...mapState(["loginAccountInfo"]),
    needChange() {
      return isNeedChangePassword(this.loginAccountInfo);
    },
    quotaItems() {
      const d = this.dashboard || {};
      const usedProjects = Number(d.used_projects || 0);
      const quotaProjects = Number(d.quota_projects || 0);
      const usedStorageMb =
        d.used_storage_mb != null ? Number(d.used_storage_mb) : this.bytesToMb(d.used_storage);
      const quotaStorageMb = Number(d.quota_storage_mb != null ? d.quota_storage_mb : d.quota_storage || 0);
      const leftProjects = Math.max(0, quotaProjects - usedProjects);
      return [
        {
          key: "projects",
          label: "工程",
          usedText: quotaProjects ? `${usedProjects} / ${quotaProjects}` : String(usedProjects),
          percent: this.usagePercent(usedProjects, quotaProjects),
          hint: quotaProjects
            ? leftProjects > 0
              ? `还可再建 ${leftProjects} 个工程`
              : "已满，请联系管理员提高工程数量"
            : "名下未归档工程",
        },
        {
          key: "storage",
          label: "存储",
          usedText: quotaStorageMb ? `${usedStorageMb} / ${quotaStorageMb} MB` : `${usedStorageMb} MB`,
          percent: this.usagePercent(usedStorageMb, quotaStorageMb),
          hint: "私有素材占用 / 账号空间",
        },
      ];
    },
    extraCountText() {
      const d = this.dashboard || {};
      const ins = Number(d.used_instructions || 0);
      const mat = Number(d.used_materials || 0);
      if (!ins && !mat) {
        return "";
      }
      return `私有指令 ${ins} 条 · 私有素材 ${mat} 个`;
    },
    projectUsages() {
      const d = this.dashboard || {};
      return d.project_usages || d.recent_projects || [];
    },
  },
  mounted() {
    this.tab = this.needChange ? "security" : "profile";
    this.load();
    document.addEventListener("visibilitychange", this.onQuotaVisibility);
  },
  activated() {
    this.load();
  },
  methods: {
    onQuotaVisibility() {
      if (document.visibilityState === "visible") {
        this.load();
      }
    },
    load() {
      this.$request.getProfile().then((res) => {
        if (res.status == "success") {
          this.form = res.data || {};
          applyAccountQuotaToLogin(this.$store, setCookie, res.data);
        }
      });
      this.quotaLoading = true;
      this.$request
        .getDashboard()
        .then((res) => {
          this.quotaLoading = false;
          if (res.status == "success") {
            const payload = res.data || {};
            this.dashboard =
              payload.quota_projects == null && payload.data ? payload.data : payload;
          }
        })
        .catch(() => {
          this.quotaLoading = false;
        });
    },
    bytesToMb(bytes) {
      const n = Number(bytes || 0);
      if (!n) return 0;
      return Math.round((n / 1024 / 1024) * 100) / 100;
    },
    usagePercent(used, quota) {
      const q = Number(quota || 0);
      if (!q) return null;
      const p = Math.round((Number(used || 0) / q) * 100);
      return Math.max(0, Math.min(100, p));
    },
    usagePair(used, quota) {
      const u = Number(used || 0);
      const q = Number(quota || 0);
      return q ? `${u} / ${q}` : String(u);
    },
    quotaRowClass({ row }) {
      if (row && (row.page_full || row.ip_full)) {
        return "row-quota-warn";
      }
      return "";
    },
    formatTime(d) {
      return d ? moment(d).format("YYYY-MM-DD HH:mm") : "";
    },
    goProjects() {
      if (this.needChange) {
        this.$message.warning("请先修改初始密码");
        this.tab = "security";
        return;
      }
      this.$router.push("/projects");
    },
    goProject(row) {
      if (this.needChange) {
        this.$message.warning("请先修改初始密码");
        this.tab = "security";
        return;
      }
      this.$router.push({ path: "/projects/projectsEdit", query: { id: row.id } });
    },
    sendSms(phone) {
      this.$request.getLoginVerifyCode({ phone }).then((res) => {
        if (res.status != "success") {
          this.$message.error(res.msg);
          return;
        }
        this.$message.success("发送成功");
      });
    },
    saveProfile() {
      this.$request
        .updateProfile({
          cn_name: this.form.cn_name,
          gender: this.form.gender,
          email: this.form.email,
          image_path: this.form.image_path,
        })
        .then((res) => {
          if (res.status != "success") {
            this.$message.error(res.msg);
            return;
          }
          this.$message.success("已保存");
          this.syncCookie({ cn_name: this.form.cn_name, image_path: this.form.image_path });
        });
    },
    async uploadAvatar(req) {
      try {
        const dataUrl = await compressAvatar(req.file);
        this.form.image_path = dataUrl;
        this.saveProfile();
      } catch (e) {
        this.$message.error("头像处理失败");
      }
    },
    savePassword() {
      this.$request
        .changePassword({ sms_code: this.pwd.sms_code, password: this.pwd.password })
        .then((res) => {
          if (res.status != "success") {
            this.$message.error(res.msg);
            return;
          }
          this.$message.success("密码已修改");
          this.syncCookie({ pwd_changed: 1 });
          this.pwd = { sms_code: "", password: "" };
          this.load();
          if (this.loginAccountInfo.user_id) {
            this.$request.getUserAuthorizedMenus({ user_id: this.loginAccountInfo.user_id }).then((menuRes) => {
              if (menuRes.status == "success") {
                this.$store.commit("setAuthorizedMenus", {
                  data: menuRes?.data?.children || [],
                  donotJump: true,
                });
              }
            });
          }
        });
    },
    savePhone() {
      this.$request
        .changePhone({
          sms_code: this.phoneForm.sms_code,
          new_phone: this.phoneForm.new_phone,
          new_sms_code: this.phoneForm.new_sms_code,
        })
        .then((res) => {
          if (res.status != "success") {
            this.$message.error(res.msg);
            return;
          }
          this.$message.success("手机号已更换");
          this.phoneForm = { sms_code: "", new_phone: "", new_sms_code: "" };
          this.load();
        });
    },
    openBindWechat() {
      this.bindVisible = true;
      this.scanned = false;
      this.$request.getQrCode({}).then((res) => {
        if (res.status != "success") {
          this.$message.error(res.msg);
          return;
        }
        this.bindEventKey = res.data.event_key;
        this.bindQr = res.data.qrcode_url;
        this.bindTimer = setInterval(() => {
          this.$request.checkWechatBind({ event_key: this.bindEventKey }).then((r) => {
            if (r.status == "success" && r.data && r.data.scanned) {
              this.scanned = true;
              this.$message.success("已扫码，请输入验证码后确认绑定");
              clearInterval(this.bindTimer);
            }
          });
        }, 3000);
      });
    },
    confirmBindWechat() {
      this.$request
        .bindWechat({ event_key: this.bindEventKey, sms_code: this.bindSms })
        .then((res) => {
          if (res.status != "success") {
            this.$message.error(res.msg);
            return;
          }
          this.$message.success("绑定成功");
          this.bindVisible = false;
          this.load();
        });
    },
    openUnbindWechat() {
      this.unbindVisible = true;
    },
    confirmUnbind() {
      this.$request.unbindWechat({ sms_code: this.unbindSms }).then((res) => {
        if (res.status != "success") {
          this.$message.error(res.msg);
          return;
        }
        this.$message.success("已解绑");
        this.unbindVisible = false;
        this.load();
      });
    },
    syncCookie(patch) {
      const next = { ...this.loginAccountInfo, ...patch };
      this.$store.commit("setLoginAccountInfo", next);
      setCookie("loginAccountInfo", JSON.stringify(next), 7 * 24 * 60 * 60 * 1000);
    },
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onQuotaVisibility);
    clearInterval(this.bindTimer);
  },
};
</script>

<style scoped lang="scss">
.personal-page {
  padding: 20px 24px 32px;
  text-align: left;
}
.pwd-alert {
  margin-bottom: 16px;
}
.identity {
  display: flex;
  align-items: center;
  padding-bottom: 20px;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f2f5;
}
.avatar-upload {
  cursor: pointer;
  ::v-deep .el-upload {
    display: block;
  }
}
.identity-text {
  margin-left: 16px;
  min-width: 0;
}
.identity-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.identity-sub {
  margin-top: 6px;
  color: #606266;
  font-size: 13px;
  span + span {
    margin-left: 16px;
  }
}
.identity-tip {
  margin-top: 4px;
  color: #c0c4cc;
  font-size: 12px;
}
.personal-tabs {
  ::v-deep .el-tabs__header {
    margin-bottom: 20px;
  }
}
.block-form {
  max-width: 480px;
}
.secure-card {
  max-width: 880px;
  padding: 16px 0 20px;
  border-bottom: 1px solid #f0f2f5;
  &:last-of-type {
    border-bottom: none;
  }
}
.secure-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.secure-desc {
  margin: 6px 0 12px;
  color: #909399;
  font-size: 13px;
}
.secure-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  .el-input {
    width: 220px;
  }
}
.quota-intro {
  margin: 0 0 16px;
  color: #909399;
  font-size: 13px;
}
.quota-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(200px, 320px));
  gap: 12px;
  margin-bottom: 12px;
}
.quota-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fafbfc;
  .q-label {
    color: #909399;
    font-size: 13px;
  }
  .q-value {
    font-size: 20px;
    font-weight: 600;
    margin: 8px 0 10px;
    color: #303133;
  }
  .q-hint {
    margin-top: 6px;
    color: #c0c4cc;
    font-size: 12px;
  }
}
.recent-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.quota-extra {
  margin: 0 0 16px;
  color: #909399;
  font-size: 12px;
}
.quota-warn-text {
  color: #e6a23c;
  font-weight: 600;
}
.quota-tag {
  margin-left: 6px;
}
::v-deep .row-quota-warn td {
  background: #fdf6ec;
}
.bind-qr {
  text-align: center;
  margin-top: 12px;
  img {
    width: 200px;
    height: 200px;
  }
}
</style>
